import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    AppBar, Toolbar, Typography, Box, Paper, Stack, TextField,
    IconButton, Avatar, CircularProgress, Accordion, AccordionSummary,
    AccordionDetails, Alert, Tooltip, Fade
} from '@mui/material';
import {
    Send as SendIcon, SmartToyOutlined as BotIcon, PersonOutlined as UserIcon,
    Code as CodeIcon, ExpandMore as ExpandMoreIcon, AttachFile as AttachFileIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import { invokeAgentAPI } from '../../apis/agent.apis.js';
import { Switch, FormControlLabel } from '@mui/material';


// Helper: Convert image to base64
const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
});



// ------------------- MAIN COMPONENT -------------------
const Agent = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState('');
    const [attachedFile, setAttachedFile] = useState(null);
    const scrollRef = useRef(null);
    const [isVisualMode, setIsVisualMode] = useState(true);
    const fileInputRef = useRef(null);


    console.log(isVisualMode)
    // --- Scroll to bottom on new message ---
    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // --- Initialize conversation ---
    useEffect(() => {
        const newId = uuidv4();
        setConversationId(newId);
    }, []);

    useEffect(() => {
        const visualMessage = "👋 Hello! I'm the **Loc8 AI Agent**.\n\nAttach an *ad creative*, and describe where you'd like to place it (e.g. 'Can you describe the attached image in billboard ID = 123 at night'). I'll generate a realistic visual mock-up.";
        const normalMessage = "👋 Hello! I'm the **Loc8 AI Agent**.\n\nI can help with data insights and function trigger. Try asking things like 'How many billboards are in Mumbai?' or 'Trigger post-campaign impression calculation'.";

        setMessages(prev => {
            // If message list is empty (on init)
            if (prev.length === 0) {
                return [{
                    role: 'assistant',
                    content: isVisualMode ? visualMessage : normalMessage
                }];
            }
            
            // If messages exist, check if it's ONLY the initial assistant message
            if (prev.length === 1 && prev[0].role === 'assistant') {
                 // Check if it's a user message (role: 'user') or an error message
                const hasUserMessages = prev.some(m => m.role === 'user');
                const hasErrorMessages = prev.some(m => m.isError);

                // If no user messages and no errors, it's safe to update the intro message
                if (!hasUserMessages && !hasErrorMessages) {
                    return [{
                        ...prev[0],
                        content: isVisualMode ? visualMessage : normalMessage
                    }];
                }
            }
            
            // Otherwise, chat is active, don't change history
            return prev;
        });
    }, [isVisualMode]); // Re-run when isVisualMode changes


    // --- File input handler ---
    const handleFileChange = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert("Please attach a valid image (JPG, PNG, etc.)");
            return;
        }

        setAttachedFile(file);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, []);

    // --- Message send handler ---
    const handleSendMessage = useCallback(async () => {
        if (!input.trim() || isLoading) return;

        if (isVisualMode && !attachedFile) {
            setMessages(prev => [
                ...prev,
                {
                    role: 'assistant',
                    isError: true,
                    content: "⚠️ Please attach an ad creative image before using Visual Simulator mode."
                }
            ]);
            return;
        }

        let userMsg = input;
        if (attachedFile) userMsg += `\n\n*[Attached Creative: ${attachedFile.name}]*`;


        setMessages((prev) => [...prev, { role: 'user', content: userMsg }]);
        setInput('');
        setIsLoading(true);

        let creativeBase64 = null;
        if (attachedFile) {
            try {
                creativeBase64 = await toBase64(attachedFile);
                setAttachedFile(null);
            } catch {
                setMessages((prev) => [...prev, {
                    role: 'assistant',
                    isError: true,
                    content: '⚠️ Failed to process the attached image. Please try again.'
                }]);
                setIsLoading(false);
                return;
            }
        }

        // Create empty assistant placeholder
        setMessages((prev) => [...prev, { role: 'assistant', content: '', thoughts: [], toolResults: [] }]);

        try {
            console.log("Invoking Agent API with:", { input, conversationId, isVisualMode });
            const response = await invokeAgentAPI(input, conversationId, creativeBase64, isVisualMode);

            if (!response.body) throw new Error("Empty response body");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop();

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const event = JSON.parse(line.substring(6));
                        if (event.type === 'metadata' && event.conversation_id && !conversationId)
                            setConversationId(event.conversation_id);
                        else updateLastMessage(event);
                    } catch (err) {
                        console.warn("Stream parse error:", err);
                    }
                }
            }
        } catch (err) {
            console.error("invokeAgentAPI error:", err);
            setMessages((prev) => [
                ...prev.slice(0, -1),
                { role: 'assistant', isError: true, content: '🚫 Could not connect to backend. Please ensure it’s running.' }
            ]);
        } finally {
            setIsLoading(false);
        }
    }, [input, attachedFile, isLoading, conversationId, isVisualMode]);

    // --- Update assistant message progressively ---
    const updateLastMessage = useCallback((event) => {
        setMessages((prev) => {
            const msgs = [...prev];
            const last = msgs[msgs.length - 1];
            if (!last) return prev;

            switch (event.type) {
                case 'thought':
                    last.thoughts = [...(last.thoughts || []), event.content];
                    break;
                case 'tool_result':
                    try {
                        const out = JSON.parse(event.content);
                        if (out.status === 'success' && out.image_base64)
                            last.generatedImage = `data:image/png;base64,${out.image_base64}`;
                    } catch { }
                    last.toolResults = [...(last.toolResults || []), { name: event.tool_name, content: event.content }];
                    break;
                case 'final_answer':
                    last.content = event.content;
                    break;
                case 'error':
                    last.isError = true;
                    last.content = `⚠️ Error: ${event.content}`;
                    break;
                default:
                    break;
            }
            return msgs;
        });
    }, []);

    // --- Enter key handler ---
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8f9fa' }}>
            <AppBar position="static" color="primary" elevation={1}>
                <Toolbar>
                    <BotIcon sx={{ mr: 1 }} />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Loc8 AI Agent — {isVisualMode ? 'Visual Simulator' : 'Normal Mode'}
                    </Typography>

                    
                </Toolbar>
            </AppBar>

            {/* --- Messages area --- */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
                <Stack spacing={2} sx={{ maxWidth: 800, mx: 'auto' }}>
                    {messages.map((msg, i) => <ChatMessage key={i} message={msg} />)}
                    <div ref={scrollRef} />
                </Stack>
            </Box>

            {/* --- Footer --- */}
            <Paper component="footer" sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Stack spacing={1} sx={{ maxWidth: 800, mx: 'auto' }}>
                    {attachedFile && (
                        <Fade in>
                            <Alert severity="info" onClose={() => setAttachedFile(null)}>
                                Attached: {attachedFile.name}
                            </Alert>
                        </Fade>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 0.5, pr: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isVisualMode}
                                    onChange={(e) => setIsVisualMode(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={isVisualMode ? "Visual Simulator" : "Normal Mode"}
                            sx={{ color: 'text.secondary' }}
                        />
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            variant="outlined"
                            placeholder="Describe your idea or billboard scene..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isLoading}
                        />
                        <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleFileChange} />
                        <Tooltip title="Attach ad creative">
                            <IconButton color={attachedFile ? "secondary" : "primary"} onClick={() => fileInputRef.current.click()} disabled={isLoading}>
                                <AttachFileIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Send message">
                            <IconButton color="primary" onClick={handleSendMessage} disabled={isLoading}>
                                {isLoading ? <CircularProgress size={22} /> : <SendIcon />}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
};

// ------------------- MESSAGE COMPONENTS -------------------

const ChatMessage = ({ message }) => {
    const isAssistant = message.role === 'assistant';
    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{
                alignItems: 'flex-start',
                justifyContent: isAssistant ? 'flex-start' : 'flex-end',
                transition: 'all 0.2s ease'
            }}
        >
            {isAssistant && <Avatar sx={{ bgcolor: 'primary.main' }}><BotIcon /></Avatar>}
            <Paper
                elevation={1}
                sx={{
                    p: 2,
                    maxWidth: '85%',
                    bgcolor: isAssistant ? 'white' : 'primary.main',
                    color: isAssistant ? 'text.primary' : 'primary.contrastText',
                    borderRadius: 3,
                    boxShadow: isAssistant ? 1 : 3
                }}
            >
                {message.generatedImage && (
                    <Box sx={{ mb: 1.5, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Generated Mock-up</Typography>
                        <img
                            src={message.generatedImage}
                            alt="Generated billboard mock-up"
                            style={{ maxWidth: '100%', borderRadius: 8, marginTop: 8 }}
                        />
                    </Box>
                )}

                {(message.thoughts?.length > 0 || message.toolResults?.length > 0) && (
                    <Accordion disableGutters sx={{ mb: 1.5, bgcolor: 'transparent', boxShadow: 'none' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: isAssistant ? 'action.active' : 'white' }} />}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>Agent Steps</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            {message.thoughts?.length > 0 && <ThoughtProcess thoughts={message.thoughts} />}
                            {message.toolResults?.length > 0 && <ToolResults toolResults={message.toolResults} />}
                        </AccordionDetails>
                    </Accordion>
                )}

                {message.content ? (
                    message.isError
                        ? <Alert severity="error" sx={{ bgcolor: 'error.light' }}>{message.content}</Alert>
                        : <Typography component="div" sx={{ fontSize: isAssistant ? '1.0rem' : 'inherit', wordWrap: 'break-word' }}><ReactMarkdown>{message.content}</ReactMarkdown></Typography>
                ) : (
                    isAssistant && !message.generatedImage && <CircularProgress size={20} />
                )}
            </Paper>
            {!isAssistant && <Avatar><UserIcon /></Avatar>}
        </Stack>
    );
};

// --- Thought process panel ---
const ThoughtProcess = ({ thoughts }) => (
    <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>Thinking...</Typography>
        <Paper variant="outlined" sx={{ p: 1, mt: 0.5, fontSize: '0.875rem', bgcolor: 'grey.50', whiteSpace: 'pre-wrap' }}>
            {thoughts.join('')}
        </Paper>
    </Box>
);

// --- Tool results panel ---
const ToolResults = ({ toolResults }) => (
    <Box>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>Tool Executed</Typography>
        {toolResults.map((res, i) => {
            let content;
            try { content = JSON.stringify(JSON.parse(res.content), null, 2); }
            catch { content = res.content; }

            return (
                <Paper key={i} variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: 'grey.50' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CodeIcon fontSize="small" />
                        <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{res.name}</Typography>
                    </Stack>
                    <Box component="pre" sx={{ m: 0, mt: 0.5, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                        {content}
                    </Box>
                </Paper>
            );
        })}
    </Box>
);

export default Agent;
